import type { ReactNode } from 'react';
import { PlainLink } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import type { FlowLinks } from '../flow/FlowDetail';
import { sourceWord } from '../flow/flow-item-words';
import { IdChip } from '../ops/IdChip';
import { OpsCompanyName } from '../ops/OpsCompanyName';
import { SHOWN_TO_SUPPORT } from '../words';
import type { ProblemFile, ProblemGroup } from './problem-types';
import { fileWords, groupHeadline, groupMeaning, placeWords } from './problem-words';

interface DetailProps {
  group: ProblemGroup;
  file: ProblemFile;
  links: FlowLinks;
}

function Fact({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="pb-fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/** "by API, Oct 7, 2:04 PM": a few words and when, when the time is known. */
const withWhen = (f: Formatter, words: string, at: string | null): string =>
  at ? `${words}, ${f.dateTime(at)}` : words;

/** "3 times (2 pages stopped)". */
function triedWords(f: Formatter, file: ProblemFile): string {
  const times = file.attempts === 1 ? 'once' : `${f.int(file.attempts)} times`;
  return file.items > 1 ? `${times} (${f.int(file.items)} pages stopped)` : times;
}

/** What the step said: the customer's sentence for a capture, the support line for a unit, or who may read it. */
function saidWords(file: ProblemFile): string {
  const nothing = file.kind === 'unit' ? SHOWN_TO_SUPPORT : 'Nothing recorded';
  return file.words ?? file.detail ?? nothing;
}

/** Why it stopped and what that means, with what a later send or the reconciler changes about it. */
function Why({ group, file }: Pick<DetailProps, 'group' | 'file'>): ReactNode {
  return (
    <p className="pb-detail__why">
      <strong>{groupHeadline(group)}.</strong> {groupMeaning(group)}
      {file.superseded
        ? ' A later send already delivered this document; closing it changes nothing for the customer.'
        : ''}
      {file.settling ? ' It is out of tries and will be parked within a minute.' : ''}
    </p>
  );
}

function Facts({ group, file, links }: DetailProps): ReactNode {
  const f = useFormat();
  const timeline = links.timeline(file.timelineId);
  return (
    <dl className="pb-facts">
      <Fact label="Company">
        <OpsCompanyName
          company={group.company}
          href={links.company(group.company.slug)}
          Link={links.Link}
        />
      </Fact>
      <Fact label="Job">{file.jobName ?? 'No job'}</Fact>
      <Fact label="File">{fileWords(f, file)}</Fact>
      <Fact label="Arrived">{withWhen(f, sourceWord(file.source), file.firstAt)}</Fact>
      <Fact label="Stopped at">{withWhen(f, placeWords([file.place]), file.parkedAt)}</Fact>
      <Fact label="Tried">{triedWords(f, file)}</Fact>
      <Fact label="What it said">{saidWords(file)}</Fact>
      <Fact label="Id">
        <IdChip
          id={file.timelineId}
          what={file.documentId ? 'document' : 'file'}
          href={timeline}
          Link={links.Link}
        />
      </Fact>
    </dl>
  );
}

/**
 * One file opened: whose it is, its job and name, how it arrived, why it stopped and what that means, how often it
 * was tried and when, and the ways on: its timeline and the company's page.
 */
export function ProblemFileDetail({ group, file, links }: DetailProps): ReactNode {
  const Link = links.Link ?? PlainLink;
  return (
    <div className="pb-detail" data-detail={file.key}>
      <Why group={group} file={file} />
      <Facts group={group} file={file} links={links} />
      <p className="pb-detail__ways">
        <Link href={links.timeline(file.timelineId)} className="pb-way">
          See what happened to it
        </Link>
        <Link href={links.company(group.company.slug)} className="pb-way">
          Open {group.company.name}
        </Link>
      </p>
    </div>
  );
}
